// AIDEV-NOTE: Types for AI-powered parts grouping feature

export enum GroupingType {
    CORE_WOUND = "CORE_WOUND",
    AREA_OF_LIFE = "AREA_OF_LIFE",
}

export interface GroupingConfig {
    label: string;
    description: string;
    partTypeFilter: string[];
    // AIDEV-NOTE: Filter by presence level (focal = actively worked with, mentioned = discussed)
    presenceLevelFilter?: ("focal" | "mentioned" | "suspected")[];
}

export const GROUPING_CONFIG: Record<GroupingType, GroupingConfig> = {
    [GroupingType.CORE_WOUND]: {
        label: "Core Wounds",
        description: "Groups exiles by the burdens and wounds they carry",
        partTypeFilter: ["Exile"], // Only include exiles
    },
    [GroupingType.AREA_OF_LIFE]: {
        label: "Life Areas",
        description:
            "Groups focal protectors by areas of life (work, relationships, family, etc.)",
        // AIDEV-NOTE: Only include protector types (all variants)
        partTypeFilter: [
            "Protector",
            "Protector (Firefighter)",
            "Protector (Manager)",
            "Protector (Self-like)",
        ],
        presenceLevelFilter: ["focal"], // Only include focal parts
    },
};

export interface PartGroup {
    name: string;
    description: string;
    partIds: string[];
    theme?: string;
}

export interface GroupingData {
    groupingType: GroupingType;
    groups: PartGroup[];
    dismissedPartIds?: string[]; // Parts dismissed from suggestions ("this is a new part")
}

export interface GroupingResponse {
    id: string;
    userId: string;
    type: GroupingType;
    partType: string[] | null;
    data: GroupingData;
    createdAt: string;
    updatedAt: string;
}

// AIDEV-NOTE: Types for group suggestion feature - suggests which group a part should be placed in
export interface GroupSuggestionRecommendation {
    type: "existing" | "new";
    groupName: string;
    groupDescription?: string; // Required when type is "new"
    confidence: "high" | "medium" | "low";
    reasoning: string;
}

export interface PartGroupSuggestion {
    partId: string;
    partName: string;
    recommendation: GroupSuggestionRecommendation;
}

export interface GroupSuggestionResponse {
    suggestions: PartGroupSuggestion[];
    existingGroups: PartGroup[];
    hasExistingGrouping: boolean;
    sessionCount: number;
}

export interface SessionGroupSuggestionResults {
    areaOfLife: GroupSuggestionResponse | null;
    coreWound: GroupSuggestionResponse | null;
}

// AIDEV-NOTE: Suggestions must keep their calculation owner across retries and client reassignment.
export interface SessionGroupSuggestions extends SessionGroupSuggestionResults {
    clientId: string;
}

export interface SessionPartForGrouping {
    id: string;
    name: string;
    type: string;
    description: string;
    attributes?: Array<{ name: string; content: string }>;
}
